import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import '../styles/submissions.css';

export interface LightboxAsset {
  url: string;
  type: 'video' | 'image';
}

interface AssetLightboxProps {
  assets: LightboxAsset[];
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export default function AssetLightbox({ assets, index, onClose, onIndexChange }: AssetLightboxProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const current = assets[index];
  if (!current) return null;

  return (
    <div className="asset-lightbox-overlay" onClick={onClose}>
      <div className="asset-lightbox-card" onClick={(e) => e.stopPropagation()}>
        <div className="asset-lightbox-media-wrap">
          <button className="asset-lightbox-close" onClick={onClose}>
            <X size={18} />
          </button>
          {current.type === 'video' ? (
            <video
              key={index}
              src={current.url}
              controls
              autoPlay
              muted
              playsInline
              className="asset-lightbox-media"
            />
          ) : (
            <img
              src={current.url}
              alt=""
              className="asset-lightbox-media"
            />
          )}
          {assets.length > 1 && (
            <>
              <button
                className="asset-lightbox-nav asset-lightbox-nav--prev"
                onClick={() => onIndexChange(Math.max(0, index - 1))}
                disabled={index === 0}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                className="asset-lightbox-nav asset-lightbox-nav--next"
                onClick={() => onIndexChange(Math.min(assets.length - 1, index + 1))}
                disabled={index === assets.length - 1}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>
        {assets.length > 1 && (
          <div className="asset-lightbox-footer">
            <div className="asset-lightbox-strip">
              {assets.map((asset, i) => (
                <button
                  key={i}
                  className={`asset-lightbox-thumb${i === index ? ' asset-lightbox-thumb--active' : ''}`}
                  onClick={() => onIndexChange(i)}
                >
                  {asset.type === 'video' ? (
                    <video src={`${asset.url}#t=0.001`} muted playsInline preload="metadata" />
                  ) : (
                    <img src={asset.url} alt="" />
                  )}
                </button>
              ))}
            </div>
            <span className="asset-lightbox-counter">
              {index + 1} / {assets.length}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
